/**
 * Publishes the REAL header height to --calmy-header-h on :root.
 *
 * Why this exists: the theme's sticky offsets were reading --c-head-h, which is
 * a hardcoded token (56px, 80px from 1150px up). That number is a guess about
 * the header, not a measurement of it — it does not know about the
 * announcement bar, a wrapped nav, a longer wordmark or a different font
 * loading in. Anything sticking to it therefore sits at the wrong offset,
 * which is exactly the empty gap above the product gallery.
 *
 * WHAT IS MEASURED is the chrome that stays on screen: the header itself, plus
 * the announcement bar only while the bar is actually pinned above it. A bar
 * that scrolls away is not something content has to clear.
 *
 * UPDATED ON: first paint, resize, font load, and any change to the
 * announcement bar — added, removed, hidden, shown, or resized. A ResizeObserver
 * covers height changes from any cause at all, including ones no event fires
 * for, which is why it is the primary signal rather than a resize listener.
 *
 * Writes only when the value actually changes: setProperty on every observer
 * callback would invalidate style on the whole document for no reason.
 */
(function () {
  "use strict";

  var root = document.documentElement;
  var last = -1;

  function header() { return document.querySelector(".chead"); }
  function bar() {
    return document.querySelector(".announcement-bar, .cann, [data-calmy-announcement]");
  }

  /* Pinned means: it stays in the viewport, so content below has to clear it.
     A statically positioned bar scrolls away and must not be counted. */
  function pinned(el) {
    if (!el) return false;
    var cs = getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    return cs.position === "fixed" || cs.position === "sticky";
  }

  function measure() {
    var h = header();
    if (!h) return;
    var cs = getComputedStyle(h);
    if (cs.display === "none") return;

    var total = h.getBoundingClientRect().height;
    var b = bar();
    if (b && pinned(b)) total += b.getBoundingClientRect().height;

    var v = Math.round(total);
    if (v === last || v <= 0) return;
    last = v;
    root.style.setProperty("--calmy-header-h", v + "px");
  }

  var ro = "ResizeObserver" in window ? new ResizeObserver(measure) : null;
  var watched = false;

  /* This script runs in <head>, BEFORE the header element exists, so the first
     measure() found nothing and every observer had nothing to attach to — the
     custom property was never written and every sticky offset silently fell
     back to its hardcoded default. Attaching is therefore retried until the
     header is actually in the document. */
  function attach() {
    var h = header();
    if (!h) return false;
    measure();
    if (ro && !watched) {
      ro.observe(h);
      var b = bar();
      if (b) ro.observe(b);
      watched = true;
    }
    return true;
  }

  if (!attach()) {
    document.addEventListener("DOMContentLoaded", attach);
    window.addEventListener("load", attach);
  }

  /* The bar can be added or removed outright, or toggled with an attribute, and
     neither fires a resize on anything the observer is already watching. */
  function observeBody() {
    if (!document.body) return;
    new MutationObserver(function () { attach(); }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["hidden", "class", "style"],
    });
  }
  if ("MutationObserver" in window) {
    if (document.body) observeBody();
    else document.addEventListener("DOMContentLoaded", observeBody);
  }

  window.addEventListener("resize", measure, { passive: true });
  window.addEventListener("orientationchange", measure);
  /* A metric-matched fallback swapping to the real face changes the header's
     height by a pixel or two, which is enough to see. */
  if (document.fonts && document.fonts.addEventListener) {
    document.fonts.addEventListener("loadingdone", measure);
  }
})();
